<template>
  <div class="d-flex flex-column">
    <div class="mb-2">
      <b-button
        variant="icon-light"
        class="shadow"
        @click="$emit('close')"
      >
        <GjIcon name="ArrowLeft" />
        {{ groupName }}
      </b-button>
    </div>
    <paper
      :title="output.name || '-'"
      body-class="mt-0"
    >
      <template v-slot:header-right>
        <b-button
          variant="icon-light"
          @click="$emit('remove')"
        >
          <GjIcon name="Delete" />
          Remove Output
        </b-button>
      </template>
      <div class="px-2 pb-1">
        <b-row class="">
          <b-col
            v-if="isHLS || isDash"
            cols="12"
            md="6"
          ><validation-observer>
            <validation-provider
              #default="{ errors }"
              name="Folder name"
              class="w-100"
              rules="required|alpha_num"
            >
              <input-field
                name="file_group-output_name"
                label="Folder name *"
                label-position="top"
              >
                <div class="w-100">
                  <b-form-input
                    id="file_group-output_name"
                    v-model="output.name"
                    class="mb-1"
                    placeholder="Enter folder name here..."
                    :state="errors.length > 0 ? false : null"
                  />
                  <custom-alert icon-name="Info">
                    {{ templateInfo.outputFolderName.message }}
                  </custom-alert>
                </div>
              </input-field>
            </validation-provider>
          </validation-observer>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <validation-provider
              name="File name"
              class="w-100"
            >
              <input-field
                name="file_group-output_name_modifier"
                label="File name *"
                label-position="top"
                :error="fileNameErr"
              >
                <div class="w-100">
                  <b-form-input
                    id="file_group-output_name_modifier"
                    v-model="output.nameModifier"
                    class="mb-1"
                    placeholder="Enter name modifier here..."
                  />
                  <custom-alert icon-name="Info">
                    {{ templateInfo.outputFileName.message }}
                  </custom-alert>
                </div>
              </input-field>
            </validation-provider>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <input-field
              name="file_group-output_preset-"
              label="Preset *"
              label-position="top"
              :error="
                output.presetId === null
                  ? 'Please select an output preset'
                  : null
              "
              :info="templateInfo.outputPreset"
              info-position="label"
              @openInfo="openInfo"
            >
              <div class="d-flex flex-column">
                <span
                  v-if="output.presetId"
                  class="mb-2 text-md pt-1 staticPresetNameHeight"
                >
                  {{ output.presetName }}
                </span>
                <div class="d-flex">
                  <b-button
                    id="file_group-output_preset"
                    variant="outline-primary"
                    @click="outputPresetsModal = true"
                  >
                    {{ output.presetId ? 'Change' : 'Select' }}
                  </b-button>
                  <b-button
                    v-if="output.presetId"
                    variant="icon-light"
                    class="ml-1"
                    @click="removeSelectedPreset"
                  >
                    Remove
                  </b-button>
                </div>
              </div>
            </input-field>
          </b-col>
        </b-row>
      </div>
    </paper>
    <b-modal
      v-model="outputPresetsModal"
      title="Select output preset"
      cancel-variant="outline-primary"
      ok-title="Select"
      body-class="p-0 position-relative"
      :ok-disabled="!selectedPreset"
      size="xl"
      @hidden="selectedPreset = null"
      @ok="selectPreset"
    >
      <output-presets-modal
        :group-type="groupType"
        :tab="tab"
        @selected="e => (selectedPreset = e)"
      />
    </b-modal>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, alphaNum } from '@validations'
import InputField from '@/components/form/InputField.vue'
import Paper from '@/components/paper/Paper.vue'
import OutputPresetsModal from './OutputPresetsModal.vue'
import templateInfo from './templateInfo'

export default {
  name: 'SingleOutputSettings',
  components: {
    Paper,
    InputField,
    ValidationProvider,
    ValidationObserver,
    OutputPresetsModal,
  },
  props: {
    output: {
      type: Object,
      default: null,
    },
    groupName: {
      type: String,
      default: null,
    },
    groupType: {
      type: Number,
      default: null,
    },
    hasDuplicateName: {
      type: Boolean,
      default: false,
    },
    hasDuplicateFolderName: {
      type: Boolean,
      default: false,
    },
    isHLS: {
      type: Boolean,
      required: true,
      default: false,
    },
    isDash: {
      type: Boolean,
      required: true,
      default: false,
    },
    tab: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      required,
      templateInfo,
      outputPresetsModal: false,
      selectedPreset: null,
      alphaNum,
    }
  },
  computed: {
    fileNameErr() {
      if (this.isHLS || this.isDash) {
        if (this.hasDuplicateFolderName) {
          if (this.hasDuplicateName) {
            return 'Name has to be unique'
          }
          return ''
        }
        return ''
      }
      if (this.hasDuplicateName) {
        return 'Name has to be unique'
      }
      return ''
    },
  },
  methods: {
    selectPreset() {
      const preset = this.selectedPreset
      this.output.presetId = preset.id
      this.output.presetName = preset.name
      this.$store
        .dispatch('outputPresets/getOutputPresetById', preset.id)
        .then(async res => {
          const presetObj = await res.data
          const videoDesc = presetObj.videoDescription
          const audioDesc = presetObj.audioDescription
          this.output.audioDescription = presetObj.audioDescription
          this.output.videoDescription = presetObj.videoDescription
          if (videoDesc) {
            this.output.videoCodec = videoDesc.codecTypeOptions.find(
              ct => ct.id === videoDesc.codecType,
            ).value
          }
          if (audioDesc) {
            this.output.audioCodec = audioDesc.codecTypeOptions.find(
              ct => ct.id === audioDesc.codecType,
            ).value
          }
        })
        .catch(() => {
          this.errorToast('Something went wrong')
        })
    },
    removeSelectedPreset() {
      this.output.presetId = null
      this.output.presetName = null
    },
    openInfo(e) {
      this.$emit('openInfo', e)
    },
  },
}
</script>

<style scoped>
.staticPresetNameHeight {
  width: 100%;
}
</style>
